/**
 * Cliente HTTP de la API.
 *
 * Se apoya en `fetch` y en las cookies de sesión, que viajan solas por ser del
 * mismo origen. Lo único que hay que añadir a mano es el testigo anti-CSRF: la
 * API emite una cookie legible por JavaScript y espera su valor en la cabecera
 * `x-csrf-token` (D-10).
 *
 * El servidor **no compara la cabecera contra esa cookie**: la recalcula a
 * partir de la sesión. La cookie es solo la forma de que el frontal conozca el
 * valor, y por eso lleva el prefijo `__Host-`, que impide que otro subdominio
 * la escriba.
 */

const BASE = '/api/v1';

import { CABECERA_CSRF, COOKIE_CSRF, esConflictoDeHueco } from '@citas/frontal';

export interface DetalleDeError {
  codigo: string;
  mensaje: string;
  detalle?: unknown;
}

/** Un error devuelto por la API, con su código para poder reaccionar. */
export class ErrorDeApi extends Error {
  constructor(
    readonly estado: number,
    readonly codigo: string,
    mensaje: string,
    readonly detalle?: unknown,
  ) {
    super(mensaje);
    this.name = 'ErrorDeApi';
  }

  /**
   * El hueco elegido se ocupó mientras el cliente rellenaba el formulario.
   *
   * Delega en `@citas/frontal` en vez de repetir la condición: qué cuenta como
   * conflicto de hueco tiene que ser lo mismo aquí y en el panel.
   */
  get esConflictoDeHueco(): boolean {
    return esConflictoDeHueco(this);
  }

  get esSesionCaducada(): boolean {
    return this.estado === 401;
  }
}

function leerCookie(nombre: string): string | null {
  const encontrada = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${nombre}=`));
  return encontrada === undefined ? null : decodeURIComponent(encontrada.slice(nombre.length + 1));
}

interface OpcionesDePeticion {
  metodo?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  cuerpo?: unknown;
}

export async function peticion<T>(ruta: string, opciones: OpcionesDePeticion = {}): Promise<T> {
  const metodo = opciones.metodo ?? 'GET';
  const cabeceras: Record<string, string> = {};

  if (opciones.cuerpo !== undefined) {
    cabeceras['content-type'] = 'application/json';
  }
  if (metodo !== 'GET') {
    const testigo = leerCookie(COOKIE_CSRF);
    if (testigo !== null) cabeceras[CABECERA_CSRF] = testigo;
  }

  let respuesta: Response;
  try {
    respuesta = await fetch(`${BASE}${ruta}`, {
      method: metodo,
      headers: cabeceras,
      credentials: 'same-origin',
      body: opciones.cuerpo === undefined ? undefined : JSON.stringify(opciones.cuerpo),
    });
  } catch {
    throw new ErrorDeApi(0, 'sin_conexion', 'No se ha podido contactar con el servidor.');
  }

  if (respuesta.status === 204) return undefined as T;

  const tipo = respuesta.headers.get('content-type') ?? '';
  if (!tipo.includes('application/json')) {
    const texto = await respuesta.text();
    if (!respuesta.ok) {
      throw new ErrorDeApi(respuesta.status, 'error_interno', 'Respuesta inesperada del servidor.');
    }
    return texto as T;
  }

  const cuerpo = (await respuesta.json()) as unknown;
  if (!respuesta.ok) {
    const error = (cuerpo as { error?: DetalleDeError }).error;
    throw new ErrorDeApi(
      respuesta.status,
      error?.codigo ?? 'error_desconocido',
      error?.mensaje ?? 'Algo ha ido mal.',
      error?.detalle,
    );
  }
  return cuerpo as T;
}
